import "../../src/style.css";
import React, { useState } from "react";
import { MultiSelect } from "react-multi-select-component";

const ALEXA_API = "https://data.whitelion.in/echo/v1/assistant/";
const GOOGLE_API = "https://data.whitelion.in/echo/v1/gHome/";
const ALEXA_CLIENT_ID =
  "amzn1.application-oa2-client.75152a7b7cc04fccbc64795d7abe15e4";

const MultiStepForm = () => {
  const qp = new URLSearchParams(window.location.search);
  const client_id = qp.get("client_id");
  const response_type = qp.get("response_type");
  const redirect_uri = qp.get("redirect_uri");
  const state = qp.get("state");
  const scope = qp.get("scope");
  const isGoogle = (qp.get("oauthType") || "").toLowerCase() === "google";

  const baseURL = isGoogle ? GOOGLE_API : ALEXA_API;

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [deviceInfo] = useState(navigator.userAgent);
  const [premises, setPremises] = useState([]);
  const [premisesWithRooms, setPremisesWithRooms] = useState([]);
  const [selectedPremise, setSelectedPremise] = useState("");
  const [roomList, setRoomList] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState([]);
  const [OAuthId, setOAuthId] = useState(() => qp.get("oAuth_id") || "");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [errorRooms, setErrorRooms] = useState(false);

  if (!client_id || !redirect_uri || !state) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          textAlign: "center",
        }}
      >
        <div className="error-box">
          <h2>⚠️ Authorization Error</h2>
          <p>
            Missing or invalid OAuth parameters. Please check your
            authentication link and try again.
          </p>
        </div>
      </div>
    );
  }

  const handleSendOtp = async () => {
    if (!email.trim()) {
      setErrorMsg("Please enter your email or phone number.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const res = await fetch(`${baseURL}send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_or_mobile: email, deviceInfo }),
      });
      const data = await res.json();
      if (data.status === 0) setErrorMsg(data.msg);
      else {
        setSuccessMsg(data.msg);
        setStep(2);
      }
    } catch {
      setErrorMsg("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setErrorMsg("Please enter the OTP.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    const body = {
      email_or_mobile: email,
      otp,
      clientId: isGoogle ? client_id : ALEXA_CLIENT_ID,
      state,
    };
    try {
      const res = await fetch(`${baseURL}verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === 0) setErrorMsg(data.msg);
      else {
        setSuccessMsg(data.msg);
        setToken(data.token);
        setPremises(data.premises || []);
        setPremisesWithRooms(data?.premisesWithRooms);
        if (data.premises?.length > 0) {
          setSelectedPremise(data.premises[0].id);
          const roomData = data?.premisesWithRooms?.find(
            (p) => p.premiseId === data.premises[0].id,
          );
          setRoomList(roomData?.rooms || []);
        }
        if (data.oAuth_id) setOAuthId(String(data.oAuth_id));
        setStep(3);
      }
    } catch {
      setErrorMsg("Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncDevices = async () => {
    if (!selectedPremise) {
      setErrorMsg("Please select a premise.");
      return;
    }
    if (isChecked && selectedRoom.length === 0) {
      setErrorRooms("Please select at least one room.");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    setErrorRooms(false);

    try {
      const roomIds =
        selectedRoom.length > 0
          ? selectedRoom.map((item) => item.value)
          : undefined;

      if (isGoogle) {
        if (!OAuthId) {
          setErrorMsg(
            "Missing oAuth_id. Open the link from Google Home again.",
          );
          setLoading(false);
          return;
        }
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${baseURL}oauth/complete`;
        form.enctype = "application/x-www-form-urlencoded";

        const fields = {
          oAuth_id: OAuthId,
          premiseId: String(selectedPremise),
          client_id,
          redirect_uri,
          state,
          response_type: response_type || "code",
          ...(scope ? { scope } : {}),
          ...(roomIds && { roomIds: JSON.stringify(roomIds) }),
        };
        Object.entries(fields).forEach(([name, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }

      const oauthQuery = new URLSearchParams({
        client_id,
        response_type: response_type || "code",
        redirect_uri,
        state,
        scope: scope || "profile",
      });

      const authRes = await fetch(`${baseURL}oauth/authorize?${oauthQuery}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          premiseId: selectedPremise,
          oAuth_id: OAuthId,
          ...(roomIds && { roomIds }),
        }),
      });
      const authData = await authRes.json();
      if (authRes.status === 200) {
        setSuccessMsg("Devices synchronized successfully!");
        window.location.href = authData.redirectUrl;
      } else {
        setErrorMsg(authData.msg || "Failed to synchronize devices.");
      }
    } catch {
      setErrorMsg(
        "An error occurred during authorization or device syncing. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="left">
        <div className="header">
          <div
            className="animation a1"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: "40px",
            }}
          >
            <img src="/images/whitelion.png" alt="logo" />
          </div>
          <h2
            className="animation a2"
            style={{ textAlign: "center", fontWeight: "600" }}
          >
            <span style={{ color: "#4285F4" }}>A</span>
            <span style={{ color: isGoogle ? "#EA4335" : "#000000" }}>i</span>
            <span style={{ color: isGoogle ? "#FBBC05" : "#000000" }}>r</span>
            <span style={{ color: isGoogle ? "#4285F4" : "#000000" }}>H</span>
            <span style={{ color: isGoogle ? "#34A853" : "#000000" }}>o</span>
            <span style={{ color: isGoogle ? "#EA4335" : "#000000" }}>m</span>
            <span style={{ color: isGoogle ? "#FBBC05" : "#000000" }}>e</span>
          </h2>
          <h4 className="animation a3" style={{ textAlign: "center" }}>
            {step === 1 && "By signing in, you are authorizing Google to control your devices. Sign in to your AirHome account."}
          </h4>
          <h4 className="animation a3" style={{ textAlign: "center" }}>
            {step === 2 && `Verify the OTP sent to ${email}.`}
          </h4>
          <h4 className="animation a3" style={{ textAlign: "center" }}>
            {step === 3 && "Synchronize your devices."}
          </h4>
        </div>

        {step === 1 && (
          <div className="form">
            <input
              type="text"
              placeholder="Email / Phone number"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-field animation a3"
            />
            <button
              className="animation a5"
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="form">
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="form-field "
            />
            <button onClick={handleVerifyOtp} disabled={loading}>
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="form">
            <select
              style={{ fontSize: "15px" }}
              className="form-field"
              value={selectedPremise}
              onChange={(e) => {
                setSelectedPremise(e.target.value);
                const roomData = premisesWithRooms?.find(
                  (p) => p.premiseId === e.target.value,
                );
                setRoomList(roomData?.rooms || []);
              }}
            >
              {premises.map((premise) => (
                <option key={premise.id} value={premise.id}>
                  {premise.name}
                </option>
              ))}
            </select>
            {selectedPremise.length > 0 && (
              <div
                className="form-check"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "12px",
                  color: "#00000066",
                }}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="enablePremise"
                  checked={isChecked}
                  onChange={() => {
                    setIsChecked(!isChecked);
                    setSelectedRoom([]);
                  }}
                />
                <label
                  className="form-check-label"
                  htmlFor="enablePremise"
                  style={{ fontWeight: "normal", fontSize: "15px" }}
                >
                  Set up for specific room only
                </label>
              </div>
            )}
            {isChecked && (
              <div className="form" style={{ marginTop: "12px" }}>
                <MultiSelect
                  options={roomList.map((room) => ({
                    label: room.roomName,
                    value: room.roomId,
                  }))}
                  value={selectedRoom}
                  onChange={setSelectedRoom}
                  labelledBy="Select"
                  overrideStrings={{ selectSomeItems: "Select Rooms" }}
                  className="dropdown-container"
                />
              </div>
            )}
            {errorRooms && (
              <p
                className="error-msg"
                style={{ color: "red", textAlign: "left" }}
              >
                {errorRooms}
              </p>
            )}
            <button onClick={handleSyncDevices} disabled={loading}>
              {loading ? "Syncing..." : "Sync Devices"}
            </button>
          </div>
        )}

        {errorMsg && (
          <p className="error-msg" style={{ color: "red" }}>
            {errorMsg}
          </p>
        )}
        {successMsg && (
          <p className="success-msg" style={{ color: "green" }}>
            {successMsg}
          </p>
        )}
      </div>
      <div className="right"></div>
    </div>
  );
};

export default MultiStepForm;
// http://localhost:3000/authenticate?client_id=amzn1.application-oa2-client.75152a7b7cc04fccbc64795d7abe15e4&response_type=code&scope=profile&redirect_uri=https%3A%2F%2Flayla.amazon.com%2Fapi%2Fskill%2Flink%2FM3CY6Y8UYW1RXE&state=AmaseyJ2YWxpZGF0aW9uQ29kZSI6ImU2ckJ2RHNtZk1VTXAvUUYvQXBMSFJEOXJ2MFB3d2FuNzdROTZpVW1pcTQ9IiwiZGlyZWN0ZWRJZCI6ImFtem4xLmFjY291bnQuQUVPVUNEVTRGTFNWVDdEUUFWMzVVSUQyV0s3QSIsInBhcnRuZXJJZCI6Imw5a0FwQXVCc0VBS01wY2pjOWNCMWFCZU0iLCJhcHBsaWNhdGlvbkRvbWFpbiI6IkFMRVhBX0NPSE8iLCJhcHBsaWNhdGlvbkRvbWFpbnMiOlsiQUxFWEFfQ09ITyJdLCJleHBpcmF0aW9uVGltZUluTWlsbGlzIjoxNzQxNjk5OTEwMDUxLCJ1c2VyU3RhdGUiOlt7ImsiOiJyZXF1ZXN0UmVhbG0iLCJ2IjoiZXUtd2VzdC0xIn0seyJrIjoic2tpbGxJZCIsInYiOiJhbXpuMS5hc2suc2tpbGwuZmE1YzYzZWMtNTVhYy00Mzg3LTg0ZTAtYmYwMWU3OTQ5ODY4In0seyJrIjoiY3NyZlRva2VuIiwidiI6Ii0xMzI3MDEyNTEifSx7ImsiOiJyZXF1ZXN0SW5ncmVzcyIsInYiOiJkcCJ9LHsiayI6ImN1c3RvbWVySWQiLCJ2IjoiQTNFMUkyUEM3RlRRUFMifSx7ImsiOiJyZXF1ZXN0RGV2aWNlRmFtaWx5IiwidiI6IkNvbXBBcHBBbmRyb2lkIn0seyJrIjoicmVxdWVzdFN0YWdlIiwidiI6InByb2QifSx7ImsiOiJza2lsbFN0YWdlIiwidiI6ImxpdmUifSx7ImsiOiJyZXF1ZXN0U3RhcnRUaW1lIiwidiI6IjE3NDE2OTYzMTAwMjkifV0sImRpcmVjdGVkSWRUeXBlIjoiT0JGVVNDQVRFRF9DVVNUT01FUl9JRCIsImdyYW50ZWRTY29wZXNJbmNsdWRlZCI6ZmFsc2V9BBBBBBBBAAAAAAAAAQA2arnsYO8M-Ar0V-0BBpxIIQAAAAAAAAAorayMdaSXkhqro4ZVG6K9q0ju7ymbMhd3Al7eDF4U5zw%3D

//http://localhost:3000/authenticate?oauthType=google&client_id=171715373261-djljul2oiqle9fbhchjo8h86g7jn27ei.apps.googleusercontent.com&oAuth_id=69d8c2c69247ae77e0df7e91&redirect_uri=https%3A%2F%2Foauth-redirect.googleusercontent.com%2Fr%2Fairhome-lifestyle-automation&state=APvkAzE6Rfk7lfVZ0x8cDJyrTvdxZ18ORDnpxiFgk1YOyNeybMTYjROxBUjl--ctRhKurGcdqoSbO0CbKQ_otyn5GJU7Op58zyEv9reBW-uXkYMgTyyboRyZPwL-7qVRoQ3Oc-ann7qL1yJAyPiIhtuKHCcBjww-nsIqiBVuS2HqAQTBwwfje5kUPnauhWb_4DhqdyGLXGbVbxEMoYDE6LhScj1O-awKBQ8vDNmNRm_wSOyDt-Ao7gVaEsVzUh96Klh_mi_nfPIu14QPxzzqZSpC8PIdORkEXE3xDQovxrojWXdtb9v0NsK-6BbDoCVCf5cc243p4TyoNrd5IbMOdybkTJsWp-qyXwvQ08avYMtJaMktjBuXyajAtC2Lc5RTF9VtC_WfqTg9Pb5CVF9xAOx1_jrzmvA1SpFpkhoKYad-7Lle4kEB034ZQXsfXzkApKE-Z-V3FvrnylhLc4uMIR1fBA1zJ-3DDvNzQnpFzWAYTV6y0VKAokOGD9Z_BnCJYpPiiGktbF1M7PXtXLd-1ceEFOgmb4Ihp7_xrMvaXUK_HizKAhG0BI9rkJPEBmppUqbQy72qOwTUeRupQEf7eWtCO66sFEc8yf65ngo&response_type=code
